<template>
  <div class="quick-map-google-info">
    <div class="cols align-items-center">
      <div class="car-icon rows align-items-center">
        <img src="@/assets/images/dashboard/car-icon.png" alt="">
      </div>
      <div class="mt-10 mb-5">
        <span class="title pr-5">{{ carTitle }}</span>
        <button v-if="showTripsButton"
                class="transparent table-action"
                :title="$t('dashboard.quick-map.info-window.show-trips')"
                @click="onShowTripsClick">
          <i class="bi bi-card-list"/>
        </button>
        <button v-if="showLoadTripsButton"
                class="transparent table-action"
                :title="$t('dashboard.quick-map.info-window.load-trips')"
                @click="onLoadTripsClick">
          <i class="bi bi-sign-turn-right"/>
        </button>
      </div>
      <span class="device-title title" v-if="carTitle !== deviceTitle">{{ deviceTitle }}</span>
      <span v-if="alarmMessage" class="alarm-container">
        <i class="bi bi-exclamation-circle-fill font-size-24 text-danger px-20"></i>
        <span class="car-alarm-text">{{alarmMessage}}</span>
      </span>
      <table class="popup-details">
        <tr v-if="lastUpdateTime && isFieldVisible(DashboardQuickMapInfoField.LastUpdate)">
          <th>{{ $t('dashboard.quick-map.info-window.last-update') }}</th>
          <td>{{ lastUpdateTimeFormatted }}</td>
        </tr>
        <tr v-if="address && isFieldVisible(DashboardQuickMapInfoField.Address)">
          <th>{{ $t('dashboard.quick-map.info-window.address') }}</th>
          <td>{{ address }}</td>
        </tr>
        <tr v-if="ignition != null && isFieldVisible(DashboardQuickMapInfoField.Ignition)">
          <th>{{ $t('dashboard.quick-map.info-window.ignition') }}</th>
          <td>{{ ignitionLabel }}</td>
        </tr>
        <tr v-if="speedStatus != null && isFieldVisible(DashboardQuickMapInfoField.Speed)">
          <th>{{ $t('dashboard.quick-map.info-window.speed') }}</th>
          <td>{{ speedLabel }}</td>
        </tr>
        <tr v-if="fuel != null && isFieldVisible(DashboardQuickMapInfoField.Fuel)">
          <th>{{ $t('dashboard.quick-map.info-window.fuel') }}</th>
          <td>{{ fuelLabel }}</td>
        </tr>
        <tr v-if="engineHours && isFieldVisible(DashboardQuickMapInfoField.EngineHours)">
          <th>{{ $t('dashboard.quick-map.info-window.engine-hours') }}</th>
          <td>{{ engineHours }}</td>
        </tr>
        <tr v-if="driver && isFieldVisible(DashboardQuickMapInfoField.Driver)">
          <th>{{ $t('dashboard.quick-map.info-window.driver') }}</th>
          <td>{{ driverLabel }}</td>
        </tr>
        <tr v-if="fuelCapStatus != null && isFieldVisible(DashboardQuickMapInfoField.FuelCapStatus)">
          <th>{{ $t('generic.params-map.fuel-cap-status') }}</th>
          <td>{{ fuelCapLabel }}</td>
        </tr>
        <tr v-for="[paramName, paramVal] in filteredGpsParamsEntries" v-bind:key="paramName">
          <th>{{ paramName }}</th>
          <td>{{ paramVal?.translationKey ? $t(paramVal.translationKey) : paramVal }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import {FuelCapStatus} from "@/assets/js/enums/FuelCapStatus.ts";
import {DashboardQuickMapInfoField} from "@/assets/js/enums/DashboardQuickMapInfoField.js";

export default {
  name: 'QuickMapInfo',
  data() {
    return {
      alarmMessage: null,
      checkAlarmTimeoutHandle: null,
    }
  },
  emits: ['showTrips', 'loadTrips'],
  props: {
    showTripsButton: [Boolean],
    showLoadTripsButton: {
      type: Boolean,
      default: false,
    },
    deviceId: [Number],
    carTitle: String,
    deviceTitle: String,
    address: String,
    speedStatus: [String, Number],
    fuel: [Number, String, null],
    engineHours: [String, null],
    driver: String,
    fuelCapStatus: [Number, null],
    lastUpdateTime: [Object, null],
    ignition: [Boolean, null],
    visibleFields: Array,
    gpsParams: {
      type: Object,
      default: Object.create({})
    },
    alarm: Function
  },
  computed: {
    DashboardQuickMapInfoField() { return DashboardQuickMapInfoField },
    ignitionLabel() {
      return this.ignition ? this.$t('generic.yes') : this.$t('generic.no');
    },
    speedLabel() {
      return typeof this.speedStatus === 'number'
          ? this.speedStatus.toFixed(0) + ' ' + this.$t('generic.kmph')
          : this.$t('dashboard.quick-map.info.' + this.speedStatus);
    },
    fuelLabel() {
      return this.fuel === '' ? '-' : (this.fuel.toFixed(0) + ' ' + this.$t('generic.litres'));
    },
    driverLabel(){
      return this.driver === '' ? '-' : this.driver ;
    },
    fuelCapLabel(){
      switch (this.fuelCapStatus) {
        case FuelCapStatus.Open:
          return this.$t('generic.params-map.fuel-cap-status.open');
        case FuelCapStatus.Close:
          return this.$t('generic.params-map.fuel-cap-status.closed');
        case FuelCapStatus.Tampering:
          return this.$t('generic.params-map.fuel-cap-status.tampering');
      }

      return '-';
    },
    lastUpdateTimeFormatted() {
      let result = '-';

      if (this.lastUpdateTime?.isValid?.()) {
        return this.lastUpdateTime.format(this.$t('settings.generic.date-time-format-moment'));
      }

      return result;
    },
    selectedVisibleFields() {
      return Array.isArray(this.visibleFields)
          ? this.visibleFields
          : DashboardQuickMapInfoField.values();
    },
    filteredGpsParamsEntries() {
      return Object.entries(this.gpsParams ?? {})
          .filter(([paramName]) => this.isGpsParamVisible(paramName));
    }
  },
  mounted() {
    if (this.alarm) {
      this.checkForAlarm();
    }
  },
  unmounted() {
    if (this.checkAlarmTimeoutHandle) {
      clearTimeout(this.checkAlarmTimeoutHandle);
    }
  },
  methods: {
    isFieldVisible(field) {
      return this.selectedVisibleFields.includes(field);
    },
    isGpsParamVisible(paramName) {
      const configurableGpsParams = {
        [this.$t('generic.params-map.battery-level')]: DashboardQuickMapInfoField.GpsBatteryLevel,
        [this.$t('generic.params-map.battery-current')]: DashboardQuickMapInfoField.BatteryCurrent,
        [this.$t('generic.params-map.last-moving-time')]: DashboardQuickMapInfoField.MotionTime,
      };
      const field = configurableGpsParams[paramName];

      return field ? this.isFieldVisible(field) : true;
    },
    checkForAlarm() {
      this.checkAlarmTimeoutHandle = setTimeout(() => {
        this.getAlarmMessage();
        this.checkForAlarm();
      }, 700);
    },
    getAlarmMessage() {
      this.alarmMessage = this.alarm();
    },
    onShowTripsClick() {
      this.$emit('showTrips');
    },
    onLoadTripsClick() {
      this.$emit('loadTrips');
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.quick-map-google-info {
  background: #FFFFFF;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.07);
  border-radius: 12px;

  .car-icon {
    width: 40px;
    height: 40px;
    padding: 10px;

    background: linear-gradient(318.27deg, #FA57EE -16.11%, #33449C 76.98%);
    border: 1px solid #FFFFFF;
    box-shadow: 0 14px 24px rgba(0, 0, 0, 0.1);
    border-radius: 35px;

    img {
      width: 100%;
    }
  }

  .title {
    font-weight: 600;
    font-size: 1rem;
    text-align: center;
    color: $default-text-color;
    line-height: 20px;
  }

  .device-title {
    margin-bottom: 15px;
  }

  .alarm-container {
    display: inline-flex;
    align-items: center;
    padding-bottom: 10px;

    .car-alarm-text {
      font-weight: 400;
      font-size: 0.9rem;
      color: #414351;
    }
  }
}
</style>
